import { TrashIcon } from "@heroicons/react/outline";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { FolderScopeProvider } from "../ChooseEntityDialog/folder-scope-context-provider";
import InputVariableConfigurator from "../InputVariableConfigurator";
import { Variable } from "../InputVariableConfigurator/types";
import { Skeleton } from "../ui/skeleton";
import BreadcrumbsPageWrapper from "../UnifiedRegistry/BreadcrumbsPageWrapper";
import { useDeleteEntity } from "../UnifiedRegistry/Dialogs/hooks/useDeleteEntity";
import useFolderPathBreadcrumbs from "../UnifiedRegistry/hooks/useFolderPathBreadcrumbs";

import { useInputVariableSetData } from "./hooks";

import { displayToast } from "@/utils/toast";
import { EntityType } from "@/queries";
import { ToastType } from "@/enums";
import { useUser } from "@/context/user-context";
import { ResourceHeader } from "@/components/ui/ResourceHeader";

const InputVariableSet = () => {
  const { setId } = useParams();
  const navigate = useNavigate();
  const { activeWorkspaceId } = useUser();
  const { deleteEntity } = useDeleteEntity();

  const {
    variableSet: initialVariableSet,
    metadata,
    isLoading,
    isSaving,
    updateMetadata,
    saveVariables,
    deleteVariableSet,
  } = useInputVariableSetData(setId);

  const [currentVariables, setCurrentVariables] = useState<Variable[]>(
    initialVariableSet?.variables || [],
  );

  // Reset local variable state whenever the active set changes
  useEffect(() => {
    setCurrentVariables(initialVariableSet?.variables || []);
  }, [setId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVariablesChange = useCallback((variables: Variable[]) => {
    setCurrentVariables(variables);
  }, []);

  const handleSave = useCallback(async () => {
    if (setId) {
      await saveVariables(currentVariables);
      displayToast("Input variable set updated", ToastType.success);
    }
  }, [setId, saveVariables, currentVariables]);

  const handleDelete = useCallback(() => {
    if (!metadata) {
      return;
    }

    deleteEntity(
      {
        id: metadata.id,
        name: metadata.name,
        type: EntityType.INPUT_VARIABLE_SET,
        path: [],
      },
      async () => {
        await deleteVariableSet();
        navigate(`/workspace/${activeWorkspaceId}/home`);
      },
    );
  }, [metadata, deleteEntity, deleteVariableSet, navigate, activeWorkspaceId]);

  const breadcrumbProps = useFolderPathBreadcrumbs(
    metadata?.folder_id ?? null,
    metadata?.name || "Variable Set",
  );
  const handleTitleEdit = useCallback(
    ({ newTitle }: { newTitle: string }) => updateMetadata({ name: newTitle }),
    [updateMetadata],
  );
  const entityTitle = {
    type: EntityType.INPUT_VARIABLE_SET,
    title: metadata?.name || "Variable Set",
    onTitleEdit: handleTitleEdit,
    isLoading,
  };

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <BreadcrumbsPageWrapper
          {...breadcrumbProps}
          className="flex h-full flex-col pb-3"
          entityTitle={entityTitle}
        >
          <div className="border-b border-gray-200 px-6 py-4">
            <Skeleton className="mb-2 h-8 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex-1 overflow-auto px-6 py-6">
            <div className="mx-auto max-w-4xl">
              <Skeleton className="mb-4 h-6 w-48" />
              <div className="space-y-2">
                <Skeleton className="h-16" />
                <Skeleton className="h-16" />
                <Skeleton className="h-16" />
              </div>
            </div>
          </div>
        </BreadcrumbsPageWrapper>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <BreadcrumbsPageWrapper
        {...breadcrumbProps}
        className="flex h-full flex-col pb-3"
        entityTitle={entityTitle}
      >
        <ResourceHeader
          type={EntityType.INPUT_VARIABLE_SET}
          titleInBreadcrumb
          isLoading={isLoading}
          title={metadata?.name || "Variable Set"}
          minimal
          onTitleEdit={handleTitleEdit}
          id={setId || ""}
          format="variable-set"
          createdAt={metadata?.created_at || new Date().toISOString()}
          actions={[
            {
              label: "Delete",
              onClick: handleDelete,
              variant: "destructiveLink",
              icon: <TrashIcon className="mr-2 h-4 w-4" />,
            },
          ]}
        />

        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex flex-1 flex-col gap-6 overflow-hidden">
            <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border bg-white shadow-sm">
              <FolderScopeProvider value={initialVariableSet.folder_id ?? null}>
                <InputVariableConfigurator
                  key={setId}
                  className="h-full w-full overflow-hidden"
                  inputVariables={initialVariableSet?.variables || []}
                  onChange={handleVariablesChange}
                  onClose={handleSave}
                  hideExportButton
                  useSaveButton
                  isLoading={isSaving}
                  title="Variables"
                  placeholder="No variables defined. Click 'Add Variable' to get started."
                  enableAIDataset
                />
              </FolderScopeProvider>
            </div>
          </div>
        </div>
      </BreadcrumbsPageWrapper>
    </div>
  );
};

export default InputVariableSet;
